import { useEffect, useState } from "react";
import { ProductCategory } from "../product-category.entity";
import { productCategoriesRepository } from "../product-categories.repository";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Shell } from "../../common/shell/shell.page";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import Swal from "sweetalert2";
import { SwalReact } from "../../common/swal-react";
import { v4 } from "uuid";
import application from "../../core/application";
import { Permission } from "../../users/user.entity";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";

export const ProductCategoriesPage = () => {
  const theme = useTheme();
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [user] = application.user.useState();
  const moduleAccess = useModuleAccess();

  application.useHasPermission([Permission.SELL, Permission.WRITE_PRODUCTS]);

  useEffect(() => {
    if (moduleAccess.loading) return;
    if (!moduleAccess.isEnabled("supplier_categories")) {
      setCategories([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await productCategoriesRepository.getAll();
        if (!cancelled) setCategories(list);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [moduleAccess.loading, moduleAccess.modules]);

  const canWrite = user?.hasPermission([Permission.WRITE_PRODUCTS]) ?? false;

  const removeCategory = async (category: ProductCategory) => {
    const result = await Swal.fire({
      title: `Excluir "${category.title}"?`,
      text: "Esta ação não pode ser desfeita.",
      confirmButtonText: "Sim, excluir",
      icon: "warning",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
    });
    if (!result.isConfirmed) return;
    await productCategoriesRepository.remove(category);
    setCategories((prev) => prev.filter((c) => c.id !== category.id));
  };

  const addCategory = async () => {
    await SwalReact.fire({
      title: "Nova categoria",
      html: (
        <TextField
          id="new-category-name"
          fullWidth
          sx={{ mt: 2 }}
          variant="outlined"
          label="Nome"
          autoFocus
        />
      ),
      showCancelButton: true,
      confirmButtonText: "Salvar",
      cancelButtonText: "Cancelar",
      preConfirm: async () => {
        const input = document.getElementById("new-category-name") as HTMLInputElement;
        if (!input?.value?.trim()) {
          SwalReact.showValidationMessage("Informe o nome da categoria.");
          return;
        }
        const category = new ProductCategory(v4(), input.value.trim(), "");
        await productCategoriesRepository.save(category);
        setCategories(await productCategoriesRepository.getAll());
      },
      focusConfirm: false,
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !SwalReact.isLoading(),
    });
  };

  if (moduleAccess.loading) {
    return (
      <Shell title="Categorias">
        <LoadingOverlay message="Carregando módulo…" subtle />
      </Shell>
    );
  }

  if (!moduleAccess.isEnabled("supplier_categories")) {
    return (
      <Shell title="Categorias">
        <PageHeader
          title="Categorias de produtos"
          description="Organize o catálogo em grupos. Categorias aparecem ao cadastrar produtos."
        />
        <ModuleLockedView
          moduleName="Fornecedores e categorias"
          groupId="supplier_categories"
          details="Ative este módulo para gerenciar categorias e fornecedores do catálogo."
          features={[
            "Organização por categorias",
            "Cadastro de fornecedores",
            "Vínculo de produtos a fornecedores",
          ]}
        />
      </Shell>
    );
  }

  if (loading) {
    return (
      <Shell title="Categorias">
        <LoadingOverlay message="Carregando categorias…" subtle />
      </Shell>
    );
  }

  return (
    <Shell title="Categorias">
      <PageHeader
        title="Categorias de produtos"
        description="Organize o catálogo em grupos. Categorias aparecem ao cadastrar produtos."
        action={
          canWrite ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={addCategory}>
              Nova categoria
            </Button>
          ) : undefined
        }
      />

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          border: 1,
          borderColor: "divider",
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.dark, 0.02)} 100%)`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -20,
            right: -20,
            opacity: 0.04,
            pointerEvents: "none",
          }}
        >
          <CategoryOutlinedIcon sx={{ fontSize: 180 }} />
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: "white",
              flexShrink: 0,
            }}
          >
            <CategoryOutlinedIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box flex={1}>
            <Typography variant="h5" fontWeight={800} letterSpacing="-0.02em">
              Categorias de produtos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Agrupe produtos por categoria para facilitar navegação e filtros.
            </Typography>
          </Box>
          <Chip
            icon={<CategoryOutlinedIcon />}
            label={`${categories.length} categoria${categories.length !== 1 ? "s" : ""}`}
            variant="outlined"
          />
        </Stack>
      </Paper>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "action.hover" }}>
              <TableCell sx={{ fontWeight: 700 }}>Categoria</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
              {canWrite && <TableCell align="right" sx={{ width: 56 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canWrite ? 3 : 2}>
                  <Box
                    sx={{
                      py: 6,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <CategoryOutlinedIcon sx={{ fontSize: 48, color: "text.disabled" }} />
                    <Typography color="text.secondary">Nenhuma categoria cadastrada.</Typography>
                    {canWrite && (
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={addCategory}>
                        Criar primeira categoria
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              categories.map((category) => (
                <TableRow
                  key={category.id}
                  sx={{ "&:last-child td": { borderBottom: 0 }, "&:hover": { bgcolor: "action.hover" } }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: "primary.main",
                          flexShrink: 0,
                        }}
                      >
                        <CategoryOutlinedIcon fontSize="small" />
                      </Box>
                      <Typography fontWeight={600}>{category.title}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {category.description || "—"}
                    </Typography>
                  </TableCell>
                  {canWrite && (
                    <TableCell align="right">
                      <Tooltip title="Excluir">
                        <IconButton size="small" color="error" onClick={() => removeCategory(category)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Shell>
  );
};
